import PropTypes from 'prop-types'
import Router from 'next/router'
import gql from 'graphql-tag'
import LinearProgress from '@material-ui/core/LinearProgress'
import StoriesAllPage from '@layouts/stories-all'
import Error from '@components/error'
import { Query } from '@apollo/client/react/components'
import storyCardFragment from '@schema/story-card'
import defaults from '@config/defaults'
import { fetchSSRData } from '@lib/ssr-fetch'

export const storiesQuery = gql`
query Stories($skip: Int, $limit: Int, $series: String) {
  storyCollection(
    limit: $limit,
    skip: $skip,
    where: {
      series: $series
    }
    order: [sys_firstPublishedAt_DESC]
  ) {
    total
    items {
      ${storyCardFragment}
    }
  }
  storiesPageCollection(limit: 1) {
    items {
      metaTitleAll
      metaDescriptionAll
      metaImageAll {
        url
      }
    }
  }
}
`

function StoriesAllWithGraph({
  query, data, ssrFetched, error: ssrError, errorObject,
}) {
  const limit = defaults.pagination

  const handlePagination = (offset) => {
    window.scrollTo(0, 260)
    Router.push(`/stories/all?offset=${offset}`)
  }

  // SSR path: data was fetched in getInitialProps
  if (ssrFetched) {
    if (ssrError) {
      return <Error displayErrorType="page" error={errorObject || { message: ssrError }} />
    }

    const { storyCollection, storiesPageCollection } = data || {}
    const meta = storiesPageCollection?.items?.[0]

    return (
      <StoriesAllPage
        {...storyCollection}
        handlePagination={handlePagination}
        limit={limit}
        offset={query ? query.offset : 0}
        {...meta}
      />
    )
  }

  // Client-side fallback (for client-side navigation)
  const variables = {
    ...query,
    limit,
  }
  if (query && query.offset) variables.skip = parseInt(query.offset, 10)

  return (
    <Query query={storiesQuery} variables={variables}>
      {({
        loading, error, data: clientData,
      }) => {
        if (error) return <Error error={error} />
        if (loading) return <LinearProgress color="secondary" />

        const {
          storyCollection,
          storiesPageCollection,
        } = clientData

        const meta = storiesPageCollection
          && storiesPageCollection.items
          && storiesPageCollection.items[0]

        return (
          <StoriesAllPage
            {...storyCollection}
            handlePagination={handlePagination}
            limit={limit}
            offset={query ? query.offset : 0}
            {...meta}
          />
        )
      }}
    </Query>
  )
}

StoriesAllWithGraph.propTypes = {
  query: PropTypes.shape({
    offset: PropTypes.string,
  }),
  data: PropTypes.object,
  ssrFetched: PropTypes.bool,
  error: PropTypes.string,
  errorObject: PropTypes.object,
}

StoriesAllWithGraph.getInitialProps = async ({ query }) => {
  const limit = defaults.pagination
  const variables = {
    ...query,
    limit,
  }
  if (query && query.offset) variables.skip = parseInt(query.offset, 10)

  return fetchSSRData({
    query,
    graphqlQuery: storiesQuery,
    variables,
  })
}

export default StoriesAllWithGraph
